'use client';
import React, { useEffect, useRef } from 'react';
import { CatCard, CatTextLink } from 'components/blocks-components';
import { Conditional, useBreakpoint } from '@cat-ecom/pcc-components';
import Image from 'next/image';
import { useSelectItemEvent } from 'hooks/analytics/use-select-item-event';
import { getFormattedSearchSelectItem } from 'components/product-comparison-grid/product-comparison-card/utils';
import CheckPriceButton from '../check-price-button/check-price-button';
import FullMatchFitment from '../full-match-fitment';
import Price from 'components/price';
import CommonAvailabilityWrapper from '../common-availability-wrapper';
import AddToCart from 'components/actions/add-to-cart/add-to-cart';
import ContactYourDealerPopover from 'components/contact-your-dealer/contact-your-dealer-popover/contact-your-dealer-popover';
import CommonAvailabilityKeysIcons from '../common-availability-keys-icons';
import ViewReplacement from 'components/actions/view-replacement/view-replacement';
import { RELATED_PARTS_GA_LIST_NAME } from '../../constants/constants';
import { useViewItemListEvent } from 'hooks/analytics/use-view-item-list-event';
import cx from 'classnames';
import styles from './product-relative.module.scss';
import RelatedPartsMobile from './related-parts-mobile';
import { PAGE_BREAKPOINTS } from 'constants/common';
const RelativeCardWrapper = ({
  locale,
  storeLocationId,
  userStoreLocationData,
  dealerInformation,
  isShowSourcingDetailEnabled,
  isDeliveryEnabled,
  isHeadlessPDPEnabled,
  catcorp,
  selectedEquipment,
  availRemoveInterimStockFlag,
  cartDetails,
  relatedPartsPriceAvailability,
  isPccRelatedParts,
  isRTL,
  currencyLdFeatureFlag,
  contactDealerBtn
}) => {
  const { fireEvent: fireSelectItemEvent } = useSelectItemEvent();
  const { fireEvent: fireViewItemListEvent } = useViewItemListEvent();
  const hasViewListItem = useRef(false);
  const isMD = useBreakpoint(PAGE_BREAKPOINTS.MD);

  const handleSelectItemGa = (part) => {
    fireSelectItemEvent({
      item: getFormattedSearchSelectItem(cartDetails?.orderHeader, part),
      partName: `${part?.partNumber}: ${part?.name}`,
      locationId: storeLocationId,
      itemSummary: part,
      isSearchPageEvent: true,
      itemListId: RELATED_PARTS_GA_LIST_NAME,
      itemListName: RELATED_PARTS_GA_LIST_NAME,
      serialNumber: '',
      fitment: ''
    });
  };

  useEffect(() => {
    const relatedViewItemList = {
      item_list_id: RELATED_PARTS_GA_LIST_NAME,
      item_list_name: RELATED_PARTS_GA_LIST_NAME
    };
    const viewRelatedPayload = {
      products: relatedPartsPriceAvailability,
      itemListData: relatedViewItemList,
      cartItemsData: cartDetails,
      cartId: cartDetails?.orderHeader?.orderId,
      isProductsData: true
    };

    if (relatedPartsPriceAvailability?.length > 0 && !hasViewListItem.current) {
      fireViewItemListEvent(viewRelatedPayload);
      hasViewListItem.current = true;
    }
  }, [cartDetails, fireViewItemListEvent, relatedPartsPriceAvailability]);

  const renderRelatdPartsMobile = () => {
    return (
      <>
        {relatedPartsPriceAvailability.map((part) => (
          <div key={part.partNumber} className="my-3">
            <RelatedPartsMobile
              key={part.partNumber}
              part={part}
              userStoreLocationData={userStoreLocationData}
              locale={locale}
              isHeadlessPDPEnabled={isHeadlessPDPEnabled}
              cartDetails={cartDetails}
              storeLocationId={storeLocationId}
              catcorp={catcorp}
              handleSelectItemGa={handleSelectItemGa}
              dealerInformation={dealerInformation}
              availRemoveInterimStockFlag={availRemoveInterimStockFlag}
              isShowSourcingDetailEnabled={isShowSourcingDetailEnabled}
              isDeliveryEnabled={isDeliveryEnabled}
              contactDealerBtn={contactDealerBtn}
              isRTL={isRTL}
              currencyLdFeatureFlag={currencyLdFeatureFlag}
            />
          </div>
        ))}
      </>
    );
  };

  return (
    <Conditional
      test={isPccRelatedParts && !isMD}
      fallback={
        <div className={`pb-3 pt-3 ${styles['product-relative']}`}>
          {relatedPartsPriceAvailability.map((part, index) => {
            const {
              partNumber = '',
              name = '',
              imageURL = '',
              shortDescription = '',
              isSerialCompatible = false,
              currency = '',
              indirectReplacement = false,
              reman = false,
              nonReturnable = false,
              type = '',
              replacementParts = [],
              enableAddToCart = false,
              seoURL = '',
              productURL = ''
            } = part;

            return (
              <CatCard
                className={`pe-2 ${styles['product-relative__card']}`}
                key={partNumber}
              >
                <div className="d-flex align-items-start">
                  <div
                    className={`${styles['product-relative__card-thumbnail']}`}
                  >
                    <Image
                      src={imageURL}
                      alt={`${partNumber}: ${name}`}
                      loading="lazy"
                      width={72}
                      height={72}
                      unoptimized
                    />
                  </div>
                  <div>
                    <CatTextLink
                      href={partNumber}
                      onClick={() => handleSelectItemGa(part)}
                      className={`cat-u-theme-typography-label-lg ${styles['product-relative__card-title']}`}
                    >
                      {`${partNumber}: ${name}`}
                    </CatTextLink>
                    <p
                      className={cx(
                        'cat-u-theme-typography-footnote mb-0',
                        styles['product-relative__card-description']
                      )}
                    >
                      {shortDescription}
                    </p>
                    {!catcorp ? (
                      <>
                        <div
                          className={`pt-2 ${styles['product-relative__card-price']}`}
                        >
                          <Conditional test={replacementParts?.length === 0}>
                            <Price
                              price={part}
                              currency={currency}
                              isRTL={isRTL}
                              currencyLdFeatureFlag={currencyLdFeatureFlag}
                              isLeftAlignPriceTag
                            />
                          </Conditional>
                        </div>
                        <Conditional
                          test={enableAddToCart}
                          fallback={
                            <div
                              className="py-2"
                              data-testid="component__contactDealerActions"
                            >
                              <ContactYourDealerPopover
                                dealerAddress={userStoreLocationData?.address}
                                responsiveBehavior
                                fullWidth
                              />
                            </div>
                          }
                        >
                          <Conditional
                            test={replacementParts?.length === 0}
                            fallback={
                              <div className="py-2">
                                <ViewReplacement
                                  partNumber={partNumber}
                                  productURL={seoURL ?? productURL}
                                  isHeadlessPDPEnabled={isHeadlessPDPEnabled}
                                />
                              </div>
                            }
                          >
                            <div className="py-2">
                              <AddToCart
                                productPrice={part}
                                productPriceData={part}
                                cartId={cartDetails?.orderHeader?.orderId}
                                locationId={storeLocationId}
                                currency={currency}
                                itemlistIdName={RELATED_PARTS_GA_LIST_NAME}
                              />
                            </div>
                          </Conditional>
                        </Conditional>
                      </>
                    ) : (
                      <div className="py-2 border-0">
                        {index === 0 ? (
                          <CheckPriceButton />
                        ) : (
                          <div style={{ visibility: 'hidden' }}>
                            <CheckPriceButton />
                          </div>
                        )}
                      </div>
                    )}
                    <div className="mb-1 d-flex flex-column">
                      <div className="align-self-end">
                        <CommonAvailabilityKeysIcons
                          indirectReplacement={indirectReplacement}
                          reman={reman}
                          nonReturnable={nonReturnable}
                          type={type}
                          isCatCorp={catcorp}
                        />
                      </div>
                      {!catcorp &&
                        replacementParts?.length === 0 &&
                        enableAddToCart && (
                          <CommonAvailabilityWrapper
                            availabilityData={part}
                            shouldDisplayLabel={false}
                            isLoading={false}
                            locale={locale}
                            dealerData={userStoreLocationData}
                            dealerInformation={dealerInformation}
                            availRemoveInterimStockFlag={
                              availRemoveInterimStockFlag
                            }
                            isShowSourcingDetailEnabled={
                              isShowSourcingDetailEnabled
                            }
                            isDeliveryEnabled={isDeliveryEnabled}
                          />
                        )}
                    </div>
                    {isSerialCompatible ? (
                      <div className="pt-1">
                        <FullMatchFitment
                          selectedEquipment={selectedEquipment}
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </CatCard>
            );
          })}
        </div>
      }
    >
      <div className="my-2">{renderRelatdPartsMobile()}</div>
    </Conditional>
  );
};

export default RelativeCardWrapper;
